import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  apiClient as defaultClient,
  type ApiClient,
  type IntegrationSummary,
} from '../api/client';
import { AppShell } from '../components/AppShell';
import { Card } from '../components/Card';
import { Spinner } from '../components/Spinner';
import { InlineAlert } from '../components/InlineAlert';
import { StatusBadge } from '../components/StatusBadge';
import { useOrg } from '../org/context';
import { useFocusMain } from '../a11y/useFocusMain';
import { toDisplayError } from '../api/errorMessages';
import { AuthedBrand } from './AuthedBrand';
import { INTEGRATION_ASSURANCE, INTEGRATION_COPY } from '../constants/integrationCopy';
import screen from './ProjectScreen.module.css';
import styles from './IntegrationSettings.module.css';

type LoadState = 'loading' | 'error' | 'ready';

export interface IntegrationSettingsProps {
  client?: ApiClient;
}

/**
 * Integration settings (Story #47, FR-10.3). Org-admin only. Presents the five
 * MVP integrations with their connection state (connected / disconnected /
 * coming-soon) and plain-language access + data-use copy. Read-only: connecting
 * and disconnecting are future connector stories, so a `coming-soon` integration
 * exposes no actionable control here.
 */
export function IntegrationSettings({ client = defaultClient }: IntegrationSettingsProps) {
  const { currentOrg } = useOrg();
  const orgId = currentOrg?.id;
  const isAdmin = currentOrg?.role === 'org_admin';
  useFocusMain();

  const [phase, setPhase] = useState<LoadState>('loading');
  const [loadMessage, setLoadMessage] = useState('');
  const [integrations, setIntegrations] = useState<IntegrationSummary[]>([]);

  const load = useCallback(async () => {
    if (!orgId || !isAdmin) return;
    setPhase('loading');
    try {
      setIntegrations(await client.getIntegrations(orgId));
      setPhase('ready');
    } catch (err) {
      setLoadMessage(toDisplayError(err).message);
      setPhase('error');
    }
  }, [client, orgId, isAdmin]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <AppShell brand={<AuthedBrand />}>
      <Card>
        <h1 className={screen.heading}>Integrations</h1>
        <p className={screen.subheading}>
          Connect an issue tracker or commerce platform. TestCraft reports defects and health to
          your tools — it never changes your store.
        </p>

        {!isAdmin ? (
          <InlineAlert severity="info">
            Integration settings are available to organization admins.
          </InlineAlert>
        ) : (
          <>
            {phase === 'loading' ? (
              <div className={screen.loading}>
                <Spinner />
                <span>Loading integrations…</span>
              </div>
            ) : null}

            {phase === 'error' ? (
              <div className={screen.loadError}>
                <InlineAlert severity="error">{loadMessage}</InlineAlert>
                <Link to="/" className={screen.backLink}>
                  Back to home
                </Link>
              </div>
            ) : null}

            {phase === 'ready' ? (
              <>
                <p className={styles.assurance} data-testid="integration-assurance">
                  {INTEGRATION_ASSURANCE}
                </p>
                <p className={screen.subheading}>
                  Issue trackers (Jira, GitHub, Linear) are connected per project.{' '}
                  <Link to="/">Open a project</Link> and choose <strong>Issue tracker</strong> to
                  connect or manage one.
                </p>
                <ul className={styles.list} aria-label="Integrations">
                  {integrations.map((integration) => {
                    const copy = INTEGRATION_COPY[integration.id];
                    return (
                      <li key={integration.id} className={styles.item}>
                        <div className={styles.itemHeader}>
                          <h2 className={styles.name}>{integration.name}</h2>
                          <StatusBadge kind="integration" status={integration.state} />
                        </div>
                        <div className={styles.copy}>
                          <p className={styles.copyRow}>
                            <span className={styles.copyLabel}>Access: </span>
                            {copy.access}
                          </p>
                          <p className={styles.copyRow}>
                            <span className={styles.copyLabel}>Data use: </span>
                            {copy.dataUse}
                          </p>
                        </div>
                        {integration.state !== 'coming-soon' ? (
                          <p className={styles.action}>
                            <Link
                              to="/"
                              className={styles.actionLink}
                              data-testid={`integration-connect-${integration.id}`}
                            >
                              {integration.state === 'connected'
                                ? 'Manage per project →'
                                : 'Connect per project →'}
                            </Link>
                          </p>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              </>
            ) : null}
          </>
        )}
      </Card>
    </AppShell>
  );
}
